import { type LabTest, type OrderRequestRow, createOrderRequestSchema } from '#shared/types';
import { Banner } from '#client/components/ui/Banner';
import { FormActions } from '#client/components/ui/FormActions';
import { ResourceFormShell } from '#client/components/ui/ResourceFormShell';
import { useFormValues } from '#client/hooks/useFormValues';
import { useResourceForm } from '#client/hooks/useResourceForm';
import { RESOURCE } from '#client/lib/routes';
import { useRepeater } from '#client/hooks/useRepeater';
import { EMPTY_DRAFT_LINE, initialDraftLines, initialRequestFields } from './order-request.logic';
import { RequestPatientCard, RequestedLinesCard } from './OrderRequestFormSections';
import { nameAndCodeOptions } from '#client/lib/form-choices';
import { useAllRecords } from '#client/hooks/useAllRecords';

const PATH = RESOURCE.orderRequests;

export function OrderRequestFormPage() {
  return (
    <ResourceFormShell<OrderRequestRow> path={PATH} noun="test request">
      {({ id, record }) => <OrderRequestForm id={id} record={record} />}
    </ResourceFormShell>
  );
}

/**
 * Taking a request off a fax.
 *
 * Almost every field is optional, deliberately. Requiring an age or a phone
 * number would mean the receptionist inventing them to get the request recorded
 * at all, and an invented age is worse than a missing one — it looks like a fact.
 */
function OrderRequestForm({
  id,
  record,
}: {
  id?: string | undefined;
  record?: OrderRequestRow | undefined;
}) {
  const tests = useAllRecords<LabTest>({ path: RESOURCE.labTests });
  const lines = useRepeater(initialDraftLines(record), EMPTY_DRAFT_LINE);
  const { values, set } = useFormValues(initialRequestFields(record));

  const form = useResourceForm({
    path: PATH,
    id,
    schema: createOrderRequestSchema,
    noun: 'Test request',
  });

  return (
    <form onSubmit={form.handleSubmit({ ...values, lines: lines.rows })} noValidate>
      <div className="c-form-stack">
        {form.formError && <Banner tone="critical">{form.formError}</Banner>}

        <RequestPatientCard values={values} onFieldChange={set} errors={form.errors} />

        <RequestedLinesCard
          lines={lines}
          testOptions={nameAndCodeOptions(tests.items)}
          linesError={form.errors.lines}
        />

        <FormActions
          cancelTo={PATH}
          submitLabel={form.isEdit ? 'Save changes' : 'Record the request'}
          submitting={form.submitting}
        />
      </div>
    </form>
  );
}
